<template>
  <form @submit.prevent novalidate="true">
    <b-card>
      <template slot="header">
        <div class="form-group form-row mb-0">
          <div class="col-lg-6">
            <treeselect :value="clientValue"
                        :clearable="false"
                        :placeholder="$t('Webportal.ClientFilter.Placeholder')"
                        :options="clientItems"
                        :disableFuzzyMatching="true"
                        :disabled="isBusy" />
          </div>
        </div>
      </template>

      <div>
        <collapsible-panel class="form-group" accordion="accordion1" :header="$t('Webportal.MarketReports.BrandLogos_')" header-class="h5" v-if="isMSMUser">
          <div class="row" v-if="clientValue">
            <div class="col-4">
              <b-form-group :label="$t('Webportal.MarketReports.BrandSelection')">
                <treeselect @input="handleBrand"
                            :limit="2"
                            :multiple="false"
                            :options="options.brands"
                            :placeholder="$t('Webportal.MarketReports.BrandPlaceholder')"
                            v-model="brandValue">
                  <label slot="option-label" slot-scope="{ node, labelClassName }" :class="labelClassName">
                    {{ node.label }} {{ node.LogoExternalImageId }}
                  </label>
                </treeselect>
              </b-form-group>
            </div>
            <div class="col">
              <b-form-group :label="$t('Webportal.MarketReports.BrandLogo_')">
                <ImageUploader  ref="logoUploader"
                                :value="getLogoUrl(brand)"
                                :upload-url="getUploadUrl(brand)"
                                :previewHeight="150"
                                @input="onInput"
                                :disabled="!brandValue || !clientValue"
                                data-vv-name="Logo" />
              </b-form-group>
            </div>
          </div>
          <div class="row">
            <div class="col right">
              <b-button id="imageUpload" type="button"
                        @click="uploadBrandLogo"
                        class="btn btn-primary"
                        :disabled="isUploadDisabled">
                <font-awesome-icon :icon="['far', 'dot-circle']" /> {{ $t("Webportal.Save") }}
              </b-button>
            </div>
          </div>
        </collapsible-panel>

        <collapsible-panel class="form-group" accordion="accordion1" :header="$t('Webportal.MarketReports.BrandColorDefaults')" header-class="h5" v-if="isMSMUser">
          <div class="tile-header">
            <div style="margin-left: auto; margin-right: 1rem;">
              <b-button type="button"
                        @click="saveBrandColorDefaults"
                        class="btn btn-secondary btn-md"
                        :disabled="!hasBrandColorDefaultChanged || isBusy || editingDisabled">
                <font-awesome-icon :icon="['far', 'dot-circle']" /> {{ $t("Webportal.Save") }}
              </b-button>
            </div>
          </div>
          <div  :class="{ 'spinner': isBusy }" 
                :style="{ 'pointer-events': isBusy ? 'none' : 'auto' }">
            <div  class="row" 
                  v-if="clientValue" 
                  v-for="(brand, index) in options.brands"
                  :key="index">
              <div class="col-2"></div>
              <div class="col-4">
                {{ brand.label }}
              </div>
              <div class="col-4">
                <dx-color-box v-model="brand.color" 
                              stylingMode="underlined"
                              @valueChanged="onBrandColorDefaultChanged($event,brand)" />
              </div>
              <div class="col-2"></div>
            </div>
          </div>
        </collapsible-panel>

        <collapsible-panel class="form-group" accordion="accordion1" :header="$t('Webportal.MarketReports.Configurations')" header-class="h5">
          <div class="tile-header">
            <div style="margin-left: auto; margin-right: 1rem;">
              <b-button type="button"
                        @click="newConfiguration"
                        class="btn btn-secondary btn-md"
                        :disabled="isBusy || editingDisabled">
                <font-awesome-icon :icon="['fas', 'plus']" /> {{ $t("Webportal.New") }}
              </b-button>
            </div>
          </div>
          <div  :class="{ 'spinner': isBusy }" 
                :style="{ 'pointer-events': isBusy ? 'none' : 'auto' }">
            <div  v-for="configuration in getConfigurations" 
                  :key="configuration.configurationId">
              <ConfigurationItem  :configuration="configuration"
                                  :options="options"
                                  @edit-configuration="editConfiguration"
                                  @delete-configuration="deleteConfiguration" />
            </div>
          </div>
        </collapsible-panel>
      </div>

      <template slot="footer">
      </template>
    </b-card>

    <b-modal ref="editConfigurationModal" scrollable centered size="xl">
      <template slot="modal-title">
        <font-awesome-icon :icon="['fas', 'pen']" />  {{ getEditConfigurationModalLabel }}
      </template>

      <div class="container modal-body" v-if="activeConfiguration">
        <div class="row">
          <div class="col-6">
            <b-form-group :label="$t('Webportal.MarketReports.ConfigurationInput')">
              <b-form-input v-model="activeConfiguration.label"
                            :state="getStateName"
                            aria-describedby="invalid-not-empty-unique"
                            :placeholder="$t('Webportal.MarketReports.ConfigurationInputPlaceholder')"
                            :disabled="activeConfiguration.isPublished"
                            size="md">
              </b-form-input>
              <b-form-invalid-feedback id="invalid-not-empty-unique">
                {{ this.$t('Webportal.MarketReports.ConfigurationInputExpectations') }}
              </b-form-invalid-feedback>
            </b-form-group>
          </div>
          <div class="col-6 right">
            <b-form-group :label="$t('Webportal.MarketReports.UseHistoryData')">
              <b-form-checkbox  v-model="activeConfiguration.useHistoryData"
                                :disabled="activeConfiguration.isPublished"
                                switch>
                                {{ activeConfiguration.useHistoryData ? $t('Webportal.Yes') : $t('Webportal.No') }}
              </b-form-checkbox>
            </b-form-group>
          </div>
        </div>
        <div class="row">
          <div class="col-4">
            <b-form-group :label="$t('Webportal.MarketReports.CountrySelection')">
              <treeselect @input="handleCountry"
                          :limit="2"
                          :multiple="false"
                          :options="options.countries"
                          :placeholder="$t('Webportal.MarketReports.CountryPlaceholder')"
                          :disabled="activeConfiguration.isPublished"
                          v-model="activeConfiguration.country"
                          />
            </b-form-group>
          </div>
          <div class="col-8">
            <b-form-group :label="$t('Webportal.MarketReports.SurveysSelection')">
              <treeselect :disabled="!activeConfiguration || !activeConfiguration.country || activeConfiguration.isPublished"
                          :disable-branch-nodes="true"
                          @input="handleSurveys"
                          :multiple="true"
                          :options="activeProjects"
                          :placeholder="$t('Webportal.MarketReports.SurveysPlaceholder')"
                          v-model="activeConfiguration.surveys"
                          />
            </b-form-group>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <b-form-group :label="$t('Webportal.MarketReports.WavesSelection')">
              <treeselect :disabled="!activeConfiguration || !activeConfiguration.country || activeConfiguration.isPublished"
                          @input="handleWaves"
                          :isDisabled="activeConfiguration.isPublished"
                          :multiple="true"
                          :options="getWaves"
                          :placeholder="$t('Webportal.MarketReports.WavesPlaceholder')"
                          v-model="activeConfiguration.waves" />
            </b-form-group>
          </div>
        </div>
        <hr/>
        <div class="row flexed">
          <div class="col-6">
            <strong>{{ this.$t('Webportal.MarketReports.WaveDetails') }}</strong>
          </div>
          <div class="col-6 right">
            <b-button type="button"
                      @click="newWaveDetail"
                      class="btn btn-secondary btn-md"
                      :disabled="activeConfiguration.isPublished">
              <font-awesome-icon :icon="['fas', 'plus']" /> {{ $t("Webportal.New") }}
            </b-button>
          </div>
        </div>
        <div  class="row" 
              v-for="(detail, index) in activeConfiguration.waveDetails" 
              :key="index">
          <div class="col-md-3">
            <b-form-group :label="$t('Webportal.MarketReports.Header_')">
              <b-form-input id="Header"
                            :disabled="activeConfiguration.isPublished"
                            v-model="detail.header"
                            size="md"
                            :state="getStateDetailHeader(detail)">
              </b-form-input>
            </b-form-group>
          </div>
          <div class="col-md-2">
            <b-form-group :label="$t('Webportal.MarketReports.Order_')">
              <b-form-input id="SortOrder"
                            :disabled="activeConfiguration.isPublished"
                            v-model="detail.sortOrder"
                            size="md"
                            :state="getStateDetailSortOrder(detail)">
              </b-form-input>
            </b-form-group>
          </div>
          <div class="col">
            <b-form-group :label="$t('Webportal.MarketReports.DetailText_')">
              <b-form-textarea  id="Text"
                                :disabled="activeConfiguration.isPublished"
                                v-model="detail.text"
                                size="md"
                                rows="3">
              </b-form-textarea>
            </b-form-group>
          </div>
          <div class="col-md-auto">
            <b-button type="button"
                      @click="deleteWaveDetail(index)"
                      style="margin-top: 1.75rem;"
                      :disabled="activeConfiguration.isPublished"
                      class="btn btn-danger btn-md">
              <font-awesome-icon icon="trash-alt" /> {{ $t('Webportal.Delete') }}
            </b-button>
          </div>
        </div>
      </div>

      <template slot="modal-footer">
        <div class="float-right">
          <b-button type="button"
                    @click="cancelConfiguration"
                    class="btn btn-secondary"
                    :disabled="!isFormChanged || !isFormValid || isBusy">
            <font-awesome-icon :icon="['far', 'ban']" /> {{ $t("Webportal.Cancel") }}
          </b-button>
          <b-button type="button"
                    @click="saveConfiguration"
                    class="btn btn-primary"
                    :disabled="!canSetConfiguration || isBusy || activeConfiguration.isPublished">
            <font-awesome-icon :icon="['far', 'dot-circle']" /> {{ $t("Webportal.Save") }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </form>
</template>

<script>
import MarketReportsService from '@/services/MarketReportsService';
import SurveysService from '@/services/SurveysService';
import ConfigurationItem from '@/components/Administration/Controls/MarketReportConfigurationItem.vue';
import CollapsiblePanel from '@/components/CollapsiblePanel.vue';
import ImageUploader from '@/components/Administration/ImageUploader.vue';

import MarketReportsWaveDetailsMixin from '@/mixins/MarketReportsWaveDetailsMixin';
import MessageBox from "@/mixins/MessageBox";
import { DxColorBox } from 'devextreme-vue';
import AuthenticationService from "@/services/AuthenticationService";

export default {
  mixins: [ MessageBox, MarketReportsWaveDetailsMixin ],

  components: { 
    CollapsiblePanel, ConfigurationItem, DxColorBox, ImageUploader
  },

  data() {
    return {
      accordion1: null,
      clientValue: undefined,
      clientItems: [],
      configurationValue: undefined,
      configurations: [],
      isBusy: false,
      filter: '',
      options: {
        brands: [],
        countries: [],
        surveys: [],
        waves: []
      },
      brandValue: null,
      brand: null,
      hasNewBrandLogo: false,
      hasBrandColorDefaultChanged: false,
      localWaves: []
    };
  },

  mounted() {
    this.load();
  },

  watch: {
    brandValue(newVal, oldVal) {
      this.hasNewBrandLogo = false;
    } 
  },

  computed: {
    isFormChanged() {
      return true;
    },
    isFormValid() {
      return true;
    },
    editingDisabled() {
      return _.isNil(this.clientValue);
    },
    getConfigurations() {
      return this.configurations;
      // let filter = this.filter;
      // return _.filter(this.configurations, function (x) { return x.label.includes(filter); });
    },
    getWaves() {
      return this.localWaves;
    },
    getStateName() {
      return this.activeConfiguration && this.configurations 
        && this.activeConfiguration.label
        && this.activeConfiguration?.label.length > 0 
        && !_.find(this.configurations, 
          x => x.id !== this.activeConfiguration.id && x.label === this.activeConfiguration.label);
    },
    canSetConfiguration() {
      return this.clientValue !== undefined
        && this.activeConfiguration !== undefined && this.activeConfiguration !== null
        && !_.isEmpty(this.activeConfiguration.label)
        && this.activeConfiguration.isPublished !== true
        && this.activeConfiguration.country !== undefined
        && this.activeConfiguration.surveys.length > 0
        && this.activeConfiguration.waves.length > 0
        && !this.hasDuplicatesOrEmpties(this.activeConfiguration.waveDetails);
    },
    getEditConfigurationModalLabel() {
      let action = this.activeConfiguration?.configurationId === -1 
        ? this.$t('Webportal.MarketReports.Create') 
        : this.$t('Webportal.Edit');
      let published = this.activeConfiguration?.isPublished 
        ? `(${this.$t('Webportal.MarketReports.Published')})` 
        : '';
      return `${action} ${this.$t('Webportal.MarketReports.Configuration')} ${published}`;
    },
    isUploadDisabled() {
      return this.isBusy || !this.hasNewBrandLogo || !this.brandValue;
    },
    isMSMUser: function() {
      return AuthenticationService.isMSMUser;
    }
  },

  methods: {
    hasDuplicatesOrEmpties(arr) {
      const setA = new Set();
      const setB = new Set();

      for (const { header, sortOrder } of arr) {
        if (!header || !sortOrder || setA.has(header) || setB.has(parseInt(sortOrder))) {
          return true;
        }
        setA.add(header);
        setB.add(parseInt(sortOrder));
      }
      
      return false;
    },

    load() {
      this.isBusy = true;
      SurveysService.getSurveyStructure()
        .then(res => {
          this.clientItems = _.map(res.data, function (item) { 
            if (_.startsWith(item.id, 'C'))
              return { id: item.id, label: item.label }; });
          
          if (this.clientValue === undefined) {
            this.clientValue = _.first(this.clientItems).id;
          }
          Promise.all([
            MarketReportsService.getMarketReportsConfigurations(this.clientValue.substring(1)),
            MarketReportsService.getMarketReportsFilters(this.clientValue.substring(1))
          ])
          .then(([resConfigs, resFilters]) => {
            this.configurations = this.getConfigurationsFixed(resConfigs.data);
            this.options = resFilters.data;
          })
          .catch(error => {
            console.error("An error occurred:", error);
            this.$root.$emit('error', error);
          })
          .finally(() => {
            this.isBusy = false;
          });
      });
    },

    newConfiguration() {
      this.activeConfiguration = {
        clientId: this.clientValue.substring(1),
        id: -1,
        name: this.$t('Webportal.MarketReports.NewConfigurationDefaultName'),
        country: '',
        surveys: [],
        waves: [],
        waveDetails: []
      };
      this.activeProjects = JSON.parse(JSON.stringify(this.options.projects));

      this.$refs.editConfigurationModal.show();
    },
    cancelConfiguration() {
      this.$refs.editConfigurationModal.hide();
      this.activeConfiguration = null;
      this.activeProjects = null;
    },
    editConfiguration(data) {
      this.activeConfiguration = _.cloneDeep(data);
      this.handleCountry(this.activeConfiguration.country);
      this.$refs.editConfigurationModal.show();
    },
    deleteConfiguration(data) {
      this.isBusy = true;
      this.$bvModal.msgBoxConfirm(
        this.$t('Webportal.MarketReports.ConfirmDeleteConfiguration'),
        this.getBasicConfirmMsgBox('Delete'))
        .then(value => {
          if (value === true) {
            MarketReportsService.deleteMarketReportsConfiguration(data.id)
              .then((res) => {
                this.configurations.splice(_.findIndex(this.configurations, ['id', data.id]), 1); 
              });
          }
        })
        .catch((error) => {
          console.error("Error while deleting Configuration: " + error)
        })
        .finally(() => {
          this.isBusy = false;
        });
    },
    saveConfiguration(event) {
      this.isBusy = true;
      if (this.activeConfiguration?.id === -1) {
        MarketReportsService.createMarketReportsConfiguration(this.activeConfiguration)
          .then((res) => {
            this.load();
          })
          .catch((error) => {
            console.error(error)
          });
      } else {
        MarketReportsService.updateMarketReportsConfiguration(this.activeConfiguration)
          .then((res) => {
            this.load();
          })
          .catch((error) => {
            console.error(error)
          });
      }

      this.$refs.editConfigurationModal.hide();
    },

    saveBrandColorDefaults(event) {
      this.isBusy = true;
      MarketReportsService.postBrandColorDefaults(
          this.clientValue.substring(1), 
          this.options.brands
            .filter(x => x.hasChanged)
            .map(x => ({ id: x.id, color: x.color })))
        .catch((error) => {
          console.error(error)
        })
        .finally(() => {
          this.load();
          this.hasBrandColorDefaultChanged = false;
          this.isBusy = false;
        });
    },
    onBrandColorDefaultChanged(event, brand) {
      this.hasBrandColorDefaultChanged = true;
      brand.hasChanged = true;
    },

    submit() {
      // console.log("SUBMIT")
    },

    handleCountry(value) {
      let surveys = JSON.parse(JSON.stringify(this.activeConfiguration.surveys));
      this.activeConfiguration.surveys = [];

      let waves = JSON.parse(JSON.stringify(this.activeConfiguration.waves));
      this.activeConfiguration.waves = [];

      this.activeProjects = JSON.parse(JSON.stringify(this.options.projects));

      let surveyIds = [];
      let allSurveys = this.activeProjects.flatMap(x => x.children);
      for (const survey of allSurveys) {
        survey.isDisabled = _.find(survey.countries, ['CountryId', value]) === undefined;
        if (!survey.isDisabled) {
          surveyIds.push(survey.id);
          if (surveys.includes(survey.id)) {
            this.activeConfiguration.surveys.push(survey.id);
          }
        }
      }

      for (const project of this.activeProjects) {
        project.children = _.sortBy(project.children, ['isDisabled', 'label']);
      }
      // this.options.surveys = _.sortBy(this.options.surveys, ['isDisabled', 'label']);

      for (const wave of this.options.waves) {
        wave.isDisabled = surveyIds.includes(wave.surveyId) === false;
        if (!wave.isDisabled && waves.includes(wave.id)) {
          this.activeConfiguration.waves.push(wave.id);
        }
      }

      this.localWaves = this.mapWaves(this.options.waves);

      this.activeConfiguration.waves = this.mapSelectedWaves(this.activeConfiguration.waves, this.localWaves);
    },

    handleSurveys(value) {
      for (const wave of this.options.waves) {
        wave.isDisabled = this.activeConfiguration.surveys.includes(wave.surveyId) === false;
      }
      
      this.localWaves = this.mapWaves(this.options.waves);
     
      this.activeConfiguration.waves = this.mapSelectedWaves(
        this.activeConfiguration.waves, 
        this.localWaves);

      let surveyIds = [];
      let allSurveys = this.activeProjects.flatMap(x => x.children);
      for (const survey of allSurveys) {
        survey.isDisabled = _.find(survey.countries, ['CountryId', value]) === undefined;
        if (!survey.isDisabled) {
          surveyIds.push(survey.id);
          if (surveys.includes(survey.id)) {
            this.activeConfiguration.surveys.push(survey.id);
          }
        }
      }
      for (const project of this.activeProjects) {
        project.children = _.sortBy(project.children, ['isDisabled', 'label']);
      }
      this.options.surveys = _.sortBy(this.options.surveys, ['isDisabled', 'label']);

      for (const wave of this.options.waves) {
        wave.isDisabled = surveyIds.includes(wave.surveyId) === false;
        if (!wave.isDisabled && waves.includes(wave.id)) {
          this.activeConfiguration.waves.push(wave.id);
        }
      }

      this.activeConfiguration.waves = _.filter(
        this.activeConfiguration.waves, 
        function(w) { return this.activeConfiguration.surveys.includes(w.surveyId); })
    },

    mapWaves(waves) {
      const groupedWaves = waves.reduce((acc, item) => {
        if (!acc[item.label]) {
          acc[item.label] = { ids: [], isDisabled: item.isDisabled };
        }
        acc[item.label].ids.push(item.id);
        return acc;
        }, {}
      );

      const mappedWaves = Object.keys(groupedWaves).map(label => ({
        id: groupedWaves[label].ids.join(','),
        label: label,
        isDisabled: groupedWaves[label].isDisabled
      }));

      return _.sortBy(mappedWaves, ['isDisabled', 'label']);
    },

    mapSelectedWaves(selectedItems, waves) {
      const wavesMap = new Map();

      // Create a map where each id (even from comma separated strings) points to its group
      waves.forEach(wave => {
        const ids = typeof wave.id === 'string' ? wave.id.split(',') : [wave.id.toString()];
        ids.forEach(id => wavesMap.set(parseInt(id, 10), wave.id));
      });

      // Map the selected items to their corresponding group
      const result = new Set();
      selectedItems.forEach(item => {
        if (wavesMap.has(item)) {
          result.add(wavesMap.get(item));
        }
      });

      return Array.from(result);
    },

    handleWaves(value) {
    },

    handleBrand(value) {
      this.brandValue = value;
      this.brand = this.options.brands.find(x => x.id === value);
      if (this.brand && !this.brand.LogoExternalImageId) {
        this.brand.LogoExternalImageId = undefined;
      }
    },

    onInput(value) {
      if(value === null || (value && value.length === 0)) {
        if (this.brand ) {
          this.brand.LogoExternalImageId = undefined;
          this.brand.delete = value === null;
        }
      }
      this.hasNewBrandLogo = this.brand.delete || !value || value.length > 0; //  this.brand.LogoExternalImageId !== undefined;
    },

    uploadBrandLogo(event) {
      this.isBusy = true;
      this.$refs.logoUploader.uploadImage()
        .then(() => {
          MarketReportsService.getMarketReportsFilters(this.clientValue.substring(1))
            .then(res => {
              this.options = res.data;
            })
            .catch(error => {
              console.error("An error occurred:", error);
              this.$root.$emit('error', error);
            });
        })
        .finally(() => {
          this.hasNewBrandLogo = false;
          this.isBusy = false;
        });
    },

    getLogoUrl() {
      return this.brand?.LogoExternalImageId 
        ? process.env.BASE_URL + `api/media/image?id=${this.brand?.LogoExternalImageId}`
        : null;
    },

    getUploadUrl(brand) {
      if (this.clientValue && brand)
        return `api/administration/market-reports/client-brand-logo?client-id=${this.clientValue.substring(1)}&brand-id=${brand.id}`;
    }
  }
}

</script>

<style scoped>
.row {
  margin-bottom: 0.5rem;
}

.flexed {
  display: flex; 
  align-items: center;
}

.modal-body {
  height: calc(80vh);
  overflow-y: auto;
}
</style>
